import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Field, inputCls } from "@/components/LegsEditor";
import { useMe } from "@/context/MeContext";
import { useApi, errMsg } from "@/lib/api";
import { OFFICE_PHONE, signatureLines } from "@/lib/format";
import { PushSetting } from "@/components/PushNotifications";
import { PageHero } from "@/components/PageHero";

export const SignatureCard = ({ person, testId }) => {
  const [first, ...rest] = signatureLines(person);
  return (
    <div className="border-l-2 border-[#5B7FD1] pl-4 text-sm leading-relaxed" data-testid={testId}>
      <p className="text-white">{first}</p>
      {rest.map((l, i) => <p key={i} className="text-zinc-400 text-xs">{l}</p>)}
    </div>
  );
};

export default function Profile() {
  const api = useApi();
  const { me, reload } = useMe();
  const [form, setForm] = useState({ name: "", title: "", phone: "", office_ext: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ name: me.name || "", title: me.title || "", phone: me.phone || "", office_ext: me.office_ext || "" });
  }, [me]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me", form);
      await reload();
      toast.success("Profile and signature saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <PageHero testId="profile-hero">
        <p className="eyebrow mb-3">Your profile</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white mb-3">Signature</h1>
        <p className="text-zinc-300 text-sm max-w-xl">This signature is added to the emails you trigger, the quote and customer PDFs you download, and your client presentations.</p>
      </PageHero>
      <div className="grid md:grid-cols-[1fr_320px] gap-8 max-w-4xl">
        <form onSubmit={save} className="space-y-4">
          <Field label="Full name"><Input className={inputCls} value={form.name} onChange={set("name")} required maxLength={120} data-testid="profile-name-input" /></Field>
          <Field label="Title"><Input className={inputCls} value={form.title} onChange={set("title")} maxLength={120} placeholder="e.g. Senior Charter Broker" data-testid="profile-title-input" /></Field>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <Field label="Mobile phone"><Input className={inputCls} type="tel" value={form.phone} onChange={set("phone")} required minLength={7} maxLength={40} placeholder="+1 212 555 0100" data-testid="profile-phone-input" /></Field>
            <Field label="Office ext."><Input className={inputCls} value={form.office_ext} onChange={set("office_ext")} maxLength={20} placeholder="Optional" data-testid="profile-ext-input" /></Field>
          </div>
          <p className="text-[11px] text-zinc-500" data-testid="profile-office-phone">Office line {OFFICE_PHONE} (same for everyone) · Email: {me.email} · Company: JetSet Group Inc</p>
          <button type="submit" className="frame-btn gold" disabled={saving} data-testid="profile-save-btn">{saving ? "Saving…" : "Save profile"}</button>
        </form>
        <div className="space-y-6 self-start">
          <div className="lux-card p-6">
            <p className="eyebrow mb-4">Preview</p>
            <SignatureCard person={{ ...form, email: me.email }} testId="profile-signature-preview" />
          </div>
          <PushSetting />
        </div>
      </div>
    </div>
  );
}
